// ============================================
// GAME LOGIC - ゲームの更新ロジック
// ============================================
// このファイルはメインゲームループの調整を担当します。
// 各システムは専用モジュールに分割されています:
// - collision-system.ts: 衝突判定
// - bullet-system.ts: 弾丸更新
// - spawn-system.ts: 敵出現管理
// - item-system.ts: アイテム更新
// - enemy-system.ts: 敵更新

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import {
    getDt,
    getPlayer,
    getParticles,
    getWave, setWave,
    getWaveEnemiesDefeated, setWaveEnemiesDefeated,
    getShieldActive, setShieldActive,
    getShieldTimer, setShieldTimer,
    getShieldCharging,
    getPlayerStateMachine,
    getLives
} from '../../store';
import { triggerWaveStartAnimation } from '../../ui/animations/animations';
import { updateStageForWave } from '../../features/background/background';
import { updateBomb } from '../../features/abilities/bomb';
import { updateCannon } from '../../features/abilities/cannon';
import { updateHoming } from '../../features/abilities/homing';
import { updateParticleArray } from '../../features/particles/particles';

// システムモジュール
import { checkCollisions } from '../systems/collision-system';
import { updateBullets, updateEnemyBullets, updateEnemyBulletsMovementOnly } from '../systems/bullet-system';
import { updateSpawnLogic } from '../systems/spawn-system';
import { updateItems } from '../systems/item-system';
import { updateEnemies } from '../systems/enemy-system';

/**
 * updateGame() - ゲームロジックのメイン更新
 */
export function updateGame(): void {
    const dt = getDt();

    // プレイヤーのステートを更新（死亡時や登場演出時も含めてステートマシンで完結させる）
    getPlayerStateMachine().update(dt);

    const player = getPlayer();
    const stateName = player.state?.name;

    // 死亡演出中（残機0）: 画面フリーズ（パーティクルのみ）
    if (stateName === 'Dying' && getLives() <= 0) {
        updateParticleArray(getParticles());
        return;
    }

    // 死亡演出中（残機あり）: 敵は動き続ける、スポーン停止、衝突判定なし
    if (stateName === 'Dying') {
        updateEnemies(dt);
        updateEnemyBulletsMovementOnly(dt);
        updateParticleArray(getParticles());
        return;
    }

    // 待機状態: 敵ドレイン中、スポーン停止、衝突判定なし
    if (stateName === 'Waiting') {
        updateEnemies(dt);
        updateEnemyBulletsMovementOnly(dt);
        updateParticleArray(getParticles());
        return;
    }

    // リスポーン出撃中: パーティクルのみ
    if (stateName === 'RespawnEntry') {
        updateParticleArray(getParticles());
        return;
    }

    // 通常フロー
    updatePlayerStatus(dt);
    updateSpawnLogic(dt);
    updateBullets(dt);
    updateEnemies(dt);
    updateEnemyBullets(dt);
    updateItems(dt);
    checkCollisions();

    // パーティクルの更新
    updateParticleArray(getParticles());
}

/**
 * updatePlayerStatus() - プレイヤーの状態とタイマーの更新
 */
function updatePlayerStatus(dt: number): void {
    const player = getPlayer();
    if (!player) {
        return;
    }

    // シールドタイマーの更新（チャージアニメーション中は一時停止）
    if (getShieldActive() && !getShieldCharging()) {
        let timer = getShieldTimer();
        timer -= dt;
        setShieldTimer(timer);
        if (timer <= 0) {
            setShieldActive(false);
        }
    }

    // ホーミングの処理
    updateHoming(dt);

    // 荷電粒子砲の処理
    updateCannon(dt);

    // ボムの更新
    updateBomb(dt);
}

/**
 * checkWaveProgress() - ウェーブ進行チェック
 */
export function checkWaveProgress(): void {
    const p = getP5();
    const waveEnemiesDefeated = getWaveEnemiesDefeated();
    const wave = getWave();

    let required = CONFIG.wave.baseEnemiesPerWave + (wave - 1) * CONFIG.wave.enemiesIncreasePerWave;
    required = p.min(required, CONFIG.wave.maxEnemiesPerWave);

    if (waveEnemiesDefeated >= required) {
        setWave(wave + 1);
        setWaveEnemiesDefeated(0);
        triggerWaveStartAnimation();

        // 背景の更新
        updateStageForWave(getWave());
    }
}
